import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWaiterGame } from '../utils/waiterGameContext';
import './WaiterInviteOverlay.css';

function WaiterAvatar() {
  // Use the branded animated waiter asset.
  // This keeps the UI consistent across the app.
  return (
    <img
      className="waiter-avatar"
      src="/animate.png"
      alt="Waiter"
      loading="eager"
      draggable={false}
    />
  );
}

export default function WaiterInviteOverlay({ restaurantId = null }) {
  const navigate = useNavigate();
  const { isInviteOpen, closeInvite, lastOrderMeta } = useWaiterGame();
  const [stage, setStage] = useState('hidden'); // hidden | entering | visible | leaving

  const message = useMemo(() => {
    const dish = lastOrderMeta?.dishName;
    const tail = dish ? `Your ${dish} is on its way.` : 'Your order is on its way.';
    return `Don’t waste your time getting bored while waiting—want to play a game?\n${tail}`;
  }, [lastOrderMeta]);

  useEffect(() => {
    if (!isInviteOpen) {
      setStage('hidden');
      return;
    }
    setStage('entering');
    const t = setTimeout(() => setStage('visible'), 260);
    return () => clearTimeout(t);
  }, [isInviteOpen]);

  const dismiss = () => {
    setStage('leaving');
    setTimeout(() => {
      closeInvite();
      setStage('hidden');
    }, 220);
  };

  const goPlay = () => {
    // close first for smoothness, then navigate.
    setStage('leaving');
    setTimeout(() => {
      closeInvite();
      setStage('hidden');

      const qs = restaurantId ? `?restaurantId=${encodeURIComponent(restaurantId)}` : '';
      navigate(`/customer/games${qs}`);
    }, 180);
  };

  if (!isInviteOpen) return null;

  return (
    <div
      className={`waiter-invite-backdrop stage-${stage}`}
      role="dialog"
      aria-modal="true"
      onClick={dismiss}
    >
      <div className="waiter-invite-card" onClick={(e) => e.stopPropagation()}>
        <div className="waiter-invite-left">
          <WaiterAvatar />
        </div>
        <div className="waiter-invite-right">
          <div className="waiter-speech">
            {message.split('\n').map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </div>

          <div className="waiter-actions">
            <button className="waiter-btn primary" onClick={goPlay} autoFocus>
              Yes, let’s play
            </button>
            <button className="waiter-btn" onClick={dismiss}>
              No thanks
            </button>
          </div>

          <button className="waiter-skip" onClick={dismiss} aria-label="Dismiss">
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
